/** @format */

import { makeStyles, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import CustomInputExtNumber from '../../../../components/CustomInput/CustomInputExtNumber';
import GridContainer from '../../../../components/Grid/GridContainer';
import GridItem from '../../../../components/Grid/GridItem';
import CheckBoxSimple from '../../../../components/Form/CheckBoxSimple';
import CustomTextButtom from '../../../../components/Buttons/CustomTextButton';
import TableHeaderContentCell from '../../../../components/Table/TableHeaderContentCell';
import adescomStyle from '../../../../assets/jss/adescom/adescomStyle';
import RemoveButton from '../../../../components/Buttons/RemoveButton';
import { FormGeneralHelper } from '../../../../helpers/FormGeneral';
import InputFormRest from '../../../../components/Form/InputFormRest';
import EditButton from '../../../../components/Buttons/EditButton';
import { tariffConstants } from '../../../../modules/tariffs/actions';
import { ItemLoaders } from '../../../../core/loaders/ItemLoaders';
import { useDispatch } from 'react-redux';
import RateSchemaInterface from '../../../../interfaces/billing/RateSchemaInterface';
import { CurrencyHelper } from '../../../../helpers/CurrencyHelper';

interface PriceThresholdContainerProps {
    isOpen: boolean;
    setPriceThresholds: (thresholds: Array<PriceThresholdEntityInterface>) => void;
    priceThresholds: Array<PriceThresholdEntityInterface>;
    rateEntity: RateSchemaInterface;
    parentComponent: string;
}

export interface PriceThresholdEntityInterface {
    id?: number | null;
    rateId?: number | null;
    initial: number | string;
    price: number | string;
    threshold: number | string;
    interval: number | string;
    billsec?: number | null;
}

const PRICE_THRESHOLD_INITIAL = {
    initial: '0.0000000000',
    price: '0.0000000000',
    threshold: '0.0000000000',
    interval: 1,
};

export const PriceThresholdContainer = (props: PriceThresholdContainerProps): React.ReactElement => {
    const [priceThresholdAdd, setPriceThresholdAdd] = useState<boolean>(false);
    const [entity, setEntity] = useState<PriceThresholdEntityInterface>(PRICE_THRESHOLD_INITIAL);
    const [entityList, setEntityList] = useState<Array<PriceThresholdEntityInterface>>([]);
    const [entityStatus, setEntityStatus] = useState<any>({
        threshold: null,
    });
    const [activeThresholdEditIndex, setActiveThresholdEditIndex] = useState<number | null>(null);

    const RATES_RANGES_CUSTOMER_SUMMARIC_URI = '/rates_ranges_customer_summarics';
    const PRICELIST_SCHEMA_ELEMENT_PRICE_THRESHOLD = '/pricelist_schema_element_price_thresholds';
    const { rateEntity, setPriceThresholds, priceThresholds } = props;

    const dispatch = useDispatch();
    const useStyle = makeStyles(adescomStyle);
    const classes = useStyle();

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRootBordered };

    const changeRateInput = (e: any, stateName: string): void => {
        if (!stateName) {
            stateName = e.target.name;
        }
        setEntity({
            ...entity,
            [stateName]: e.target.value,
        });
    };

    const editThreshold = (params: { index: number }): void => {
        const threshold = entityList[params.index];
        setActiveThresholdEditIndex(params.index);
        setEntity(threshold);
        setEntityStatus({ threshold: null });
    };

    const updateCallback = (
        event: React.FormEvent<HTMLInputElement>,
        stateName: string,
        typeField: string,
        typeCallback: string,
        required: boolean,
        vMax: number,
        vMin?: number
    ): void => {
        const tmp = { ...entity };
        const numbers = /^[0-9]+$/;
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        if (event.target.value.match(numbers) || event.target.value === '') {
            FormGeneralHelper.validateFormsHook(tmp, setEntity, entityStatus, setEntityStatus, event, stateName, typeField, require, vMax, vMin);
        }
    };

    const addPricelistThresholdToList = (): void => {
        setEntityList([...entityList, { ...entity }]);
        setEntity(PRICE_THRESHOLD_INITIAL);
        props.setPriceThresholds([...entityList, { ...entity }]);
    };

    const removeThreshold = (param: { index: number }): void => {
        const newList = entityList.filter((el, idx) => {
            if (idx !== param.index) {
                return el;
            }
        });

        if (activeThresholdEditIndex === param.index) {
            setEntity(PRICE_THRESHOLD_INITIAL);
            setEntityStatus({ threshold: null });
            setActiveThresholdEditIndex(null);
        }

        setEntityList(newList);
        props.setPriceThresholds(newList);
    };

    const saveThresholdEdit = (): void => {
        let newList = [...entityList];
        if (activeThresholdEditIndex !== null) {
            newList[activeThresholdEditIndex] = { ...entity };
        }

        setEntity(PRICE_THRESHOLD_INITIAL);
        setEntityStatus({ threshold: null });
        setActiveThresholdEditIndex(null);
        setEntityList(newList);
        props.setPriceThresholds(newList);
    };

    const cancelThresholdEdit = (): void => {
        setEntity(PRICE_THRESHOLD_INITIAL);
        setEntityStatus({ threshold: null });
        setActiveThresholdEditIndex(null);
    };

    const loadRatesRangesCustomerSummaric = useCallback((): void => {
        const itemLoaders = new ItemLoaders(dispatch);

        if (rateEntity && rateEntity.id) {
            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(RATES_RANGES_CUSTOMER_SUMMARIC_URI)),
                tariffConstants.LIST,
                [{ name: 'rateId', value: rateEntity.id }],
                [],
                (data: any) => {
                    if (data && data['hydra:member'] && data['hydra:member'].length > 0) {
                        const list = data['hydra:member'].map((el: any) => {
                            return el;
                        });

                        setEntityList(list);
                        setPriceThresholds(list);
                        setPriceThresholdAdd(true);
                    }
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        }
    }, [dispatch, setPriceThresholds, rateEntity, RATES_RANGES_CUSTOMER_SUMMARIC_URI]);

    const loadRatesRangesCustomerSummaricForPricelist = useCallback((): void => {
        const itemLoaders = new ItemLoaders(dispatch);

        if (rateEntity && rateEntity.id) {
            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath(PRICELIST_SCHEMA_ELEMENT_PRICE_THRESHOLD)),
                tariffConstants.LIST,
                [{ name: 'rateSchemaId', value: rateEntity.id }],
                [],
                (data: any) => {
                    if (data && data['hydra:member'] && data['hydra:member'].length > 0) {
                        const list = data['hydra:member'].map((el: any) => {
                            return { id: el.id, ...JSON.parse(el.element) };
                        });
                        setEntityList(list);
                        setPriceThresholds(list);
                        setPriceThresholdAdd(true);
                    }
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        }
    }, [dispatch, setPriceThresholds, rateEntity, PRICELIST_SCHEMA_ELEMENT_PRICE_THRESHOLD]);

    const onBlurHandler = (e: any): void => {
        const parsedValue = CurrencyHelper.numericToStandardPln(e.target.value, 10, true, '.');
        setEntity({ ...entity, [e.target.name]: parsedValue });
        e.target.value = parsedValue;
    };

    useEffect(() => {
        if (props.rateEntity && props.parentComponent === 'discount') {
            loadRatesRangesCustomerSummaric();
        }

        if (props.rateEntity && props.parentComponent === 'pricelist') {
            loadRatesRangesCustomerSummaricForPricelist();
        }
    }, [props.rateEntity, props.parentComponent, loadRatesRangesCustomerSummaric, loadRatesRangesCustomerSummaricForPricelist]);

    useEffect(() => {
        if (!props.isOpen && priceThresholdAdd) {
            setPriceThresholdAdd(false);
            setEntity(PRICE_THRESHOLD_INITIAL);
            setEntityList([]);
            setEntityStatus({
                threshold: null,
            });
        }
    }, [props.isOpen, priceThresholdAdd]);

    useEffect(() => {
        console.log(priceThresholdAdd, priceThresholds);
    }, [priceThresholdAdd, priceThresholds]);

    return (
        <GridContainer>
            <GridItem xs={props.parentComponent === 'pricelist' ? 12 : 12} style={{ padding: 0 }}>
                <CheckBoxSimple
                    checked={priceThresholdAdd}
                    label={'pricelists:price_threshold.active'}
                    callback={(): void => {
                        setPriceThresholdAdd(!priceThresholdAdd);
                        if (priceThresholdAdd) {
                            setPriceThresholds([]);
                        }
                        setEntity(PRICE_THRESHOLD_INITIAL);
                        setEntityList([]);
                        setEntityStatus({ threshold: null });
                        setActiveThresholdEditIndex(null);
                    }}
                />
            </GridItem>
            <GridItem xs={props.parentComponent === 'pricelist' ? 12 : 3} spacing={0}>
                {priceThresholdAdd && (
                    <GridContainer>
                        <GridItem xs={props.parentComponent === 'pricelist' ? 6 : 12}>
                            <CustomInputExtNumber
                                onBlurHandler={onBlurHandler}
                                onChange={changeRateInput}
                                name="initial"
                                labelText="pricelists:price_threshold.initial"
                                required={true}
                                value={entity.initial}
                                decimalLimit={10}
                            />
                        </GridItem>
                        <GridItem xs={props.parentComponent === 'pricelist' ? 6 : 12}>
                            <CustomInputExtNumber
                                onBlurHandler={onBlurHandler}
                                onChange={changeRateInput}
                                name="price"
                                labelText="pricelists:price_threshold.price"
                                required={true}
                                value={entity.price}
                                decimalLimit={10}
                            />
                        </GridItem>
                        <GridItem xs={props.parentComponent === 'pricelist' ? 6 : 12}>
                            <CustomInputExtNumber
                                onBlurHandler={onBlurHandler}
                                onChange={changeRateInput}
                                name="threshold"
                                labelText="pricelists:price_threshold.threshold"
                                required={true}
                                value={entity.threshold}
                                decimalLimit={10}
                            />
                        </GridItem>
                        <GridItem xs={props.parentComponent === 'pricelist' ? 6 : 12}>
                            <InputFormRest
                                stateName={'interval'}
                                displayFieldName={'pricelists:price_threshold.interval'}
                                value={entity.interval}
                                typeCallback={FormGeneralHelper.TYPE_NUMBER}
                                typeField={FormGeneralHelper.TYPE_NUMBER}
                                callback={updateCallback}
                                required={true}
                                vMin={1}
                                vMax={999999}
                            />
                        </GridItem>
                        <GridItem xs={12}>
                            {activeThresholdEditIndex === null ? (
                                <CustomTextButtom
                                    size={'sm'}
                                    callback={addPricelistThresholdToList}
                                    callbackParam={{}}
                                    label="pricelists:price_threshold.add_threshold"
                                />
                            ) : (
                                <GridContainer>
                                    <CustomTextButtom
                                        size={'sm'}
                                        callback={saveThresholdEdit}
                                        callbackParam={{}}
                                        label="pricelists:price_threshold.save_edit"
                                    />
                                    <CustomTextButtom
                                        size={'sm'}
                                        callback={cancelThresholdEdit}
                                        callbackParam={{}}
                                        label="pricelists:price_threshold.cancel"
                                    />
                                </GridContainer>
                            )}
                        </GridItem>
                    </GridContainer>
                )}
            </GridItem>
            <GridItem xs={props.parentComponent === 'pricelist' ? 12 : 9} spacing={0}>
                {entityList.length > 0 && (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="left" style={{ width: 40 }}>
                                    <TableHeaderContentCell classes={tableStickyHeader} textAlign="center" title="clients:c.commands" />
                                </TableCell>
                                <TableCell align="center">
                                    <TableHeaderContentCell
                                        classes={tableStickyHeader}
                                        textAlign="center"
                                        title="pricelists:price_threshold.initial"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <TableHeaderContentCell classes={tableStickyHeader} textAlign="center" title="pricelists:price_threshold.price" />
                                </TableCell>
                                <TableCell align="center">
                                    <TableHeaderContentCell
                                        classes={tableStickyHeader}
                                        textAlign="center"
                                        title="pricelists:price_threshold.threshold"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <TableHeaderContentCell
                                        classes={tableStickyHeader}
                                        textAlign="center"
                                        title="pricelists:price_threshold.interval"
                                    />
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entityList.map((el, index) => {
                                return (
                                    <TableRow key={`price_threshold_${index}`}>
                                        <TableCell classes={tableCellRoot}>
                                            <EditButton
                                                callback={editThreshold}
                                                callbackParam={{ index: index }}
                                                tooltipTitle={'pricelists:price_threshold.edit'}
                                            />
                                            <RemoveButton
                                                callback={removeThreshold}
                                                callbackParam={{ index: index }}
                                                tooltipTitle={'pricelists:price_threshold.remove'}
                                            />
                                        </TableCell>
                                        <TableCell classes={tableCellRoot} align="center">
                                            {el.initial}
                                        </TableCell>
                                        <TableCell classes={tableCellRoot} align="center">
                                            {el.price}
                                        </TableCell>
                                        <TableCell classes={tableCellRoot} align="center">
                                            {el.threshold}
                                        </TableCell>
                                        <TableCell classes={tableCellRoot} align="center">
                                            {el.interval}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </GridItem>
        </GridContainer>
    );
};
